import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import { useNotificationContext, useTranslate, undoableEventEmitter } from "react-admin";

const defaultAnchorOrigin = {
  vertical: "top" as const,
  horizontal: "right" as const,
};

const CustomNotification = () => {
  const { notifications, takeNotification } = useNotificationContext();
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<any>(undefined);
  const translate = useTranslate();

  useEffect(() => {
    const beforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      const confirmationMessage = "";
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    if (messageInfo?.notificationOptions?.undoable) {
      window.addEventListener("beforeunload", beforeunload);
    }

    if (notifications.length && !messageInfo) {
      setMessageInfo(takeNotification());
      setOpen(true);
    } else if (notifications.length && messageInfo && open) {
      setOpen(false);
    }

    return () => {
      if (messageInfo?.notificationOptions?.undoable) {
        window.removeEventListener("beforeunload", beforeunload);
      }
    };
  }, [notifications, messageInfo, open, takeNotification]);

  const handleRequestClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    if (messageInfo?.notificationOptions?.undoable) {
      undoableEventEmitter.emit("end", { isUndo: false });
    }
    setMessageInfo(undefined);
  }, [messageInfo]);

  const handleUndo = useCallback(() => {
    undoableEventEmitter.emit("end", { isUndo: true });
    setOpen(false);
  }, []);

  if (!messageInfo) return null;

  const { message, type: typeFromMessage, notificationOptions } = messageInfo;
  const {
    autoHideDuration: autoHideDurationFromMessage,
    messageArgs,
    undoable,
    anchorOrigin: anchorOriginFromMessage,
  } = notificationOptions;

  const severity = typeFromMessage || "info";
  const translatedMessage =
    message && typeof message === "string" ? translate(message, messageArgs) : message;
  const undoButton = undoable ? (
    <Button color="inherit" size="small" onClick={handleUndo}>
      {translate("ra.action.undo")}
    </Button>
  ) : undefined;

  // 'info' covers both explicit type:'info' and the omitted-type default
  // (useNotify() always defaults type to 'info' before we ever see it),
  // so it's treated as the neutral/default case: MUI's plain dark Snackbar
  // bar, no severity coloring. success/warning/error get the colored Alert.
  if (severity === "info") {
    return (
      <Snackbar
        open={open}
        autoHideDuration={autoHideDurationFromMessage ?? 4000}
        disableWindowBlurListener={undoable}
        anchorOrigin={anchorOriginFromMessage || defaultAnchorOrigin}
        TransitionProps={{ onExited: handleExited }}
        onClose={handleRequestClose}
        message={translatedMessage}
        action={undoButton}
      />
    );
  }

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDurationFromMessage ?? 4000}
      disableWindowBlurListener={undoable}
      anchorOrigin={anchorOriginFromMessage || defaultAnchorOrigin}
      TransitionProps={{ onExited: handleExited }}
      onClose={handleRequestClose}
    >
      <Alert severity={severity} variant="filled" elevation={6} sx={{ width: "100%" }} action={undoButton}>
        {translatedMessage}
      </Alert>
    </Snackbar>
  );
};

export default CustomNotification;
